import { cn, focusHaloFlush, panelMotion } from "@pax/ui";
import { ChevronLeft, ChevronRight, type LucideIcon, X, ZoomIn, ZoomOut } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { formatDuration } from "@/features/evals/components";
import { JsonCode } from "./JsonCode";

export interface TimelineEvent {
  atMs: number;
  /** A `fill-` class for its tick. */
  fill: string;
  label: string;
  /** Its time is placed rather than recorded. */
  estimated?: boolean;
  detail: unknown;
  /** It read in place, shown by default, with `detail` a switch away as it was sent. */
  rendered?: ReactNode;
}

export interface TimelineBand {
  startMs: number;
  endMs: number;
  tone: string;
  striped?: boolean;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 256;
const ZOOM_STEP = 2;
const WHEEL_ZOOM_RATE = 0.004;
const PADDING = 6;
/** How near the pointer an event must be, in pixels, to be the one it points at. */
const REACH = 8;
const AXIS_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 30000, 60000];
const AXIS_GAP = 64;

const STRIPES = "bg-[repeating-linear-gradient(135deg,transparent_0_3px,rgb(0_0_0/0.22)_3px_5px)]";

/**
 * Events on one horizontal line of time, over bands of what was going on. Hold Ctrl or ⌘ and
 * scroll, or pinch, to zoom around the pointer; press an event, or step with the arrow keys, to
 * read it in full below.
 */
export function EventTimeline({
  totalMs,
  bands,
  events,
}: {
  totalMs: number;
  bands: TimelineBand[];
  events: TimelineEvent[];
}) {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const width = useWidth(scroller);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const anchor = useRef<{ ms: number; pointerX: number } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [pointed, setPointed] = useState<number | null>(null);
  const [raw, setRaw] = useState(false);

  const trackWidth = Math.max(0, width * zoom - PADDING * 2);
  const x = (ms: number) => PADDING + (totalMs ? (ms / totalMs) * trackWidth : 0);
  const msAt = (px: number) => ((px - PADDING) / Math.max(1, trackWidth)) * totalMs;

  const zoomAround = (target: number, pointerX = (scroller?.clientWidth ?? 0) / 2) => {
    if (!scroller) return;
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, target));
    anchor.current = { ms: msAt(scroller.scrollLeft + pointerX), pointerX };
    setZoom(next);
  };
  const zoomRef = useRef(zoomAround);
  zoomRef.current = zoomAround;

  useLayoutEffect(() => {
    const held = anchor.current;
    if (!scroller || !held) return;
    scroller.scrollLeft = PADDING + (held.ms / Math.max(1, totalMs)) * trackWidth - held.pointerX;
    anchor.current = null;
  }, [scroller, trackWidth, totalMs]);

  useEffect(() => {
    if (!scroller) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      zoomRef.current(
        zoom * Math.exp(-event.deltaY * WHEEL_ZOOM_RATE),
        event.clientX - scroller.getBoundingClientRect().left,
      );
    };
    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, [scroller, zoom]);

  useEffect(() => {
    if (pointed === null) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Meta" || event.key === "Control") setSelected(pointed);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pointed]);

  const nearest = (event: MouseEvent<HTMLElement>) => {
    const px = event.clientX - event.currentTarget.getBoundingClientRect().left;
    let best: number | null = null;
    let bestGap = REACH;
    events.forEach((candidate, index) => {
      const gap = Math.abs(x(candidate.atMs) - px);
      if (gap <= bestGap) {
        best = index;
        bestGap = gap;
      }
    });
    return best;
  };

  const select = (index: number | null) => {
    setSelected(index);
    const at = index === null ? null : events[index];
    if (!scroller || !at) return;
    const px = x(at.atMs);
    if (px < scroller.scrollLeft + 16 || px > scroller.scrollLeft + scroller.clientWidth - 16)
      scroller.scrollTo({ left: px - scroller.clientWidth / 2, behavior: "smooth" });
  };
  const step = (by: number) =>
    select(
      Math.min(events.length - 1, Math.max(0, (selected ?? (by > 0 ? -1 : events.length)) + by)),
    );
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    step(event.key === "ArrowRight" ? 1 : -1);
  };

  const msPerPx = totalMs / Math.max(1, trackWidth);
  const axisStep = AXIS_STEPS.find(candidate => candidate / msPerPx >= AXIS_GAP) ?? 60000;
  const axisTicks = Array.from(
    { length: Math.floor(totalMs / axisStep) + 1 },
    (_, index) => index * axisStep,
  );
  const shown = pointed ?? selected;
  const chosen = selected === null ? undefined : events[selected];

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">
          {shown !== null && events[shown]
            ? `${events[shown].label} · ${events[shown].estimated ? "≈" : ""}${formatDuration(events[shown].atMs)}`
            : `${events.length} events · press one to read it`}
        </span>
        <span className="flex shrink-0 items-center gap-0.5">
          <IconButton
            label="Zoom out"
            icon={ZoomOut}
            disabled={zoom <= MIN_ZOOM}
            onClick={() => zoomAround(zoom / ZOOM_STEP)}
          />
          <span className="w-9 text-center tabular-nums">
            {zoom < 10 ? zoom.toFixed(1) : Math.round(zoom)}×
          </span>
          <IconButton
            label="Zoom in"
            icon={ZoomIn}
            disabled={zoom >= MAX_ZOOM}
            onClick={() => zoomAround(zoom * ZOOM_STEP)}
          />
        </span>
      </div>
      <div
        ref={setScroller}
        className="overflow-x-auto overscroll-x-contain rounded-xl bg-foreground/[0.03]"
        title="Hold Ctrl or ⌘ and move to read the event under the pointer; add scrolling, or pinch, to zoom"
      >
        <div
          role="slider"
          aria-label="Events"
          aria-valuemin={1}
          aria-valuemax={events.length}
          aria-valuenow={(selected ?? 0) + 1}
          aria-valuetext={chosen?.label ?? "No event chosen"}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerMove={event => {
            const under = nearest(event);
            setPointed(under);
            if (under !== null && (event.metaKey || event.ctrlKey)) setSelected(under);
          }}
          onPointerLeave={() => setPointed(null)}
          onClick={event => select(nearest(event))}
          className={cn("relative h-[4.5rem] cursor-crosshair rounded-xl", focusHaloFlush)}
          style={{ width: width * zoom }}
        >
          {bands.map(band => (
            <div
              key={`${band.startMs}-${band.endMs}`}
              className={cn(
                "absolute top-2 h-2.5 rounded-full",
                band.tone,
                band.striped && STRIPES,
              )}
              style={{
                left: x(band.startMs),
                width: Math.max(2, x(band.endMs) - x(band.startMs) - 1),
              }}
            />
          ))}
          <svg
            className="pointer-events-none absolute inset-x-0 top-6"
            width={width * zoom}
            height={24}
            aria-hidden
          >
            {events.map((event, index) => {
              const at = x(event.atMs);
              const lit = index === selected || index === pointed;
              return (
                <rect
                  // biome-ignore lint/suspicious/noArrayIndexKey: events keep their order
                  key={index}
                  x={at - (lit ? 1 : 0.5)}
                  y={lit ? 0 : 4}
                  width={lit ? 2 : 1}
                  height={lit ? 24 : 16}
                  className={cn(event.fill, selected !== null && !lit && "opacity-50")}
                />
              );
            })}
          </svg>
          {axisTicks.map(ms => (
            <span
              key={ms}
              className="pointer-events-none absolute bottom-1 -translate-x-1/2 text-[10px] text-muted-foreground/70 tabular-nums"
              style={{ left: x(ms) }}
            >
              {formatDuration(ms)}
            </span>
          ))}
        </div>
      </div>
      {chosen && selected !== null && (
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-xs text-muted-foreground">
              Event {selected + 1} of {events.length} · {chosen.label} · at{" "}
              {chosen.estimated ? "≈" : ""}
              {formatDuration(chosen.atMs)}
            </p>
            <span className="flex shrink-0 items-center gap-0.5">
              {chosen.rendered !== undefined && (
                <span className="mr-1.5 flex rounded-full bg-foreground/[0.05] p-0.5 text-[11px]">
                  {[false, true].map(asSent => (
                    <button
                      key={String(asSent)}
                      type="button"
                      aria-pressed={raw === asSent}
                      onClick={() => setRaw(asSent)}
                      className={cn(
                        "rounded-full px-2 py-0.5",
                        raw === asSent
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                        focusHaloFlush,
                        panelMotion,
                      )}
                    >
                      {asSent ? "Raw" : "In context"}
                    </button>
                  ))}
                </span>
              )}
              <IconButton
                label="Previous event"
                icon={ChevronLeft}
                disabled={selected === 0}
                onClick={() => step(-1)}
              />
              <IconButton
                label="Next event"
                icon={ChevronRight}
                disabled={selected === events.length - 1}
                onClick={() => step(1)}
              />
              <IconButton label="Close this event" icon={X} onClick={() => select(null)} />
            </span>
          </div>
          {chosen.rendered !== undefined && !raw ? (
            chosen.rendered
          ) : (
            <JsonCode value={chosen.detail} />
          )}
        </div>
      )}
    </div>
  );
}

function IconButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`grid size-5 place-items-center rounded-full hover:bg-foreground/5 hover:text-foreground disabled:opacity-30 ${focusHaloFlush} ${panelMotion}`}
    >
      <Icon className="size-3" />
    </button>
  );
}

function useWidth(element: HTMLElement | null) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!element) return;
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
}
